import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import { useSite } from "@/context/SiteContext";
import {
  CheckCircle2, AlertTriangle, Camera, Loader2, ShieldCheck, Trash2, SprayCan, CalendarClock,
  Wrench,
} from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-SG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

// Equipment cleaning log — cross-equipment history, open-issue flags,
// schedule-free frequency stats, and supervisor issue resolution.
export default function CleaningLogTab({ equipment = [] }) {
  const { user } = useAuth();
  const { notify } = useDialog();
  // Scope every equipment / log query to the master site filter.
  // When the header filter shows "All sites" the tab reverts to
  // showing every equipment the user has access to; picking a
  // specific site trims the dropdown and log query to that site's
  // equipment only.
  const { activeSiteId, isAllSites } = useSite();
  const canResolve = ["owner", "manager", "supervisor"].includes(user?.role);
  const [eqId, setEqId] = useState("");
  const [openOnly, setOpenOnly] = useState(false);
  const [logs, setLogs] = useState([]);
  const [maintLogs, setMaintLogs] = useState([]);
  const [maintReasons, setMaintReasons] = useState({});
  const [kind, setKind] = useState("all");        // "all" | "cleaning" | "maintenance"
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);
  const [logMaintOpen, setLogMaintOpen] = useState(false);

  // Site-scoped equipment list: keep archived items out AND, when a
  // single site is selected in the header, drop equipment from any
  // other site so the dropdown only offers what's actually on-site.
  const activeEquipment = useMemo(() => {
    const nonArchived = equipment.filter((e) => !e.is_archived);
    if (isAllSites || !activeSiteId) return nonArchived;
    return nonArchived.filter((e) => e.site_id === activeSiteId);
  }, [equipment, activeSiteId, isAllSites]);

  // If the currently-selected equipment vanishes when the site
  // filter changes, clear the selection so we don't fetch stats
  // for an unreachable piece of kit.
  useEffect(() => {
    if (eqId && !activeEquipment.some((e) => e.id === eqId)) setEqId("");
  }, [activeEquipment, eqId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (eqId) params.equipment_id = eqId;
      if (!isAllSites && activeSiteId) params.site_id = activeSiteId;
      if (openOnly) params.open_issues_only = true;
      const [logsRes, maintRes] = await Promise.all([
        api.get("/cleaning/logs", { params }),
        // Maintenance logs — same equipment_id / site scoping, no
        // openOnly filter (maintenance doesn't have open-issue state).
        api.get("/maintenance/logs", {
          params: {
            ...(eqId ? { equipment_id: eqId } : {}),
            ...(!isAllSites && activeSiteId ? { site_id: activeSiteId } : {}),
          },
        }).catch(() => ({ data: { logs: [], reason_labels: {} } })),
      ]);
      setLogs(logsRes.data.logs || []);
      setMaintLogs(maintRes.data.logs || []);
      setMaintReasons(maintRes.data.reason_labels || {});
      if (eqId) {
        const { data: s } = await api.get("/cleaning/stats", { params: { equipment_id: eqId } });
        setStats(s);
      } else setStats(null);
    } catch (e) { console.debug("cleaning logs load failed", e); }
    finally { setLoading(false); }
  }, [eqId, openOnly, activeSiteId, isAllSites]);
  useEffect(() => { load(); }, [load]);

  // Interleaved timeline. Cleaning logs get `_kind="cleaning"` and a
  // sortable timestamp; maintenance logs get `_kind="maintenance"`.
  // Filter by the tab chip and openOnly.
  const timeline = useMemo(() => {
    const cleaning = (logs || []).map((l) => ({
      ...l, _kind: "cleaning", _at: l.cleaned_at || l.created_at || "",
    }));
    const maint = (maintLogs || []).map((l) => ({
      ...l, _kind: "maintenance", _at: l.performed_at || l.created_at || "",
    }));
    let out = [];
    if (kind === "cleaning") out = cleaning;
    else if (kind === "maintenance") out = maint;
    else out = [...cleaning, ...maint];
    out.sort((a, b) => (b._at || "").localeCompare(a._at || ""));
    return out;
  }, [logs, maintLogs, kind]);

  const openIssueCount = logs.filter((l) => l.issue?.status === "open").length;

  const submitMaintenance = async (form) => {
    try {
      await api.post("/maintenance/logs", form);
      await notify({ title: "Maintenance recorded", variant: "ok" });
      setLogMaintOpen(false);
      load();
    } catch (e) {
      await notify({ title: "Couldn't log maintenance", message: formatErr(e), variant: "err" });
    }
  };

  return (
    <div data-testid="cleaning-log-tab">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <SprayCan size={16} style={{ color: "var(--vb-text-2)" }} />
          <span className="text-sm opacity-70">Cleaning & Maintenance records</span>
        </div>
        <select className="vb-input" style={{ maxWidth: 260 }} value={eqId} onChange={(e) => setEqId(e.target.value)} data-testid="cleaning-eq-filter">
          <option value="">All equipment</option>
          {activeEquipment.map((e) => <option key={e.id} value={e.id}>{e.name}{e.serial_number ? ` · ${e.serial_number}` : ""}</option>)}
        </select>
        {/* Kind filter chips — All / Cleaning / Maintenance. */}
        <div className="inline-flex rounded-md overflow-hidden" style={{ border: "1px solid var(--vb-border)" }}>
          {[
            ["all", "All"],
            ["cleaning", "Cleaning"],
            ["maintenance", "Maintenance"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              className="px-3 py-1.5 text-xs"
              style={{
                background: kind === k ? "var(--vb-accent)" : "transparent",
                color: kind === k ? "#fff" : "var(--vb-text-2)",
              }}
              data-testid={`tab-kind-${k}`}
            >{label}</button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} data-testid="cleaning-open-only" />
          Open issues only {openIssueCount > 0 && !openOnly && <span className="vb-pill err text-[10px]">{openIssueCount}</span>}
        </label>
        <button
          className="vb-btn ghost ml-auto"
          onClick={() => setLogMaintOpen(true)}
          disabled={activeEquipment.length === 0}
          data-testid="log-maintenance-btn"
          title="Record a maintenance event (blade change, gasket swap, sanitation, etc.)"
        >
          <Wrench size={14} /> Log maintenance
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="cleaning-stats">
          <StatCard icon={CalendarClock} label="Last cleaned" value={stats.days_since_last != null ? `${stats.days_since_last}d ago` : "—"} sub={fmt(stats.last_cleaned_at)} />
          <StatCard icon={SprayCan} label="Cleans (30 days)" value={stats.count_last_30d ?? 0} />
          <StatCard icon={CalendarClock} label="Avg interval" value={stats.avg_interval_days != null ? `${stats.avg_interval_days}d` : "—"} />
          <StatCard icon={CheckCircle2} label="Total logged" value={stats.total ?? 0} />
        </div>
      )}

      {loading ? (
        <div className="opacity-60 text-sm py-8 text-center">Loading…</div>
      ) : timeline.length === 0 ? (
        <div className="vb-card p-8 text-center opacity-70" data-testid="cleaning-logs-empty">
          No records yet for this filter. Operators log cleaning by scanning an equipment QR code; supervisors log maintenance from the button above.
        </div>
      ) : (
        <div className="space-y-2" data-testid="cleaning-logs-list">
          {timeline.map((l) => l._kind === "maintenance" ? (
            <MaintenanceRow key={`m-${l.id}`} log={l} />
          ) : (
            <div key={l.id} className="vb-card p-4" data-testid={`cleaning-log-${l.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <strong>{l.equipment_name}</strong>
                    {l.outcome === "issue"
                      ? <span className={`vb-pill text-[10px] inline-flex items-center gap-1 ${l.issue?.status === "open" ? "err" : "ok"}`}>
                          <AlertTriangle size={10} /> {l.issue?.status === "open" ? "Issue — open" : "Issue — resolved"}
                        </span>
                      : <span className="vb-pill ok text-[10px] inline-flex items-center gap-1"><CheckCircle2 size={10} /> Completed</span>}
                  </div>
                  <div className="text-xs opacity-70 mt-1">
                    {fmt(l.cleaned_at)} · by {l.cleaned_by_name || "—"}
                    {l.protocol_title ? ` · ${l.protocol_title}` : ""}
                  </div>
                  {l.notes && <p className="text-sm mt-1 mb-0">{l.notes}</p>}
                  {l.photos?.length > 0 && <PhotoStrip urls={l.photos} />}

                  {l.issue && (
                    <div className="mt-2 p-2 rounded text-sm" style={{ background: "var(--vb-bg-2)" }}>
                      <div className="flex items-center gap-1 font-semibold" style={{ color: "var(--vb-error)" }}>
                        <AlertTriangle size={13} /> Issue reported
                      </div>
                      <p className="mt-1 mb-1">{l.issue.description}</p>
                      {l.issue.photos?.length > 0 && <PhotoStrip urls={l.issue.photos} />}
                      {l.issue.status === "closed" && (
                        <div className="mt-2 pt-2 border-t text-xs" style={{ borderColor: "var(--vb-border)" }}>
                          <span className="inline-flex items-center gap-1" style={{ color: "var(--vb-accent)" }}>
                            <ShieldCheck size={12} /> Closed by {l.issue.closed_by_name} · {fmt(l.issue.closed_at)}
                          </span>
                          {l.issue.resolution_notes && <p className="mt-1 mb-0 opacity-80">{l.issue.resolution_notes}</p>}
                          {l.issue.resolution_photos?.length > 0 && <PhotoStrip urls={l.issue.resolution_photos} />}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {l.issue?.status === "open" && canResolve && (
                  <button className="vb-btn sm flex-shrink-0" onClick={() => setResolving(l)} data-testid={`cleaning-resolve-${l.id}`}>
                    <ShieldCheck size={13} /> Resolve issue
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {logMaintOpen && (
        <LogMaintenanceForm
          equipment={activeEquipment}
          defaultEqId={eqId}
          reasonLabels={{
            scheduled: "Scheduled",
            wear: "Wear",
            break: "Breakdown",
            tracking_tension: "Tracking & tension verified",
            sanitation: "Sanitation",
            ...maintReasons,
          }}
          performerDefault={user?.name || ""}
          onSubmit={submitMaintenance}
          onClose={() => setLogMaintOpen(false)}
        />
      )}

      {resolving && (
        <ResolveIssueModal log={resolving} onClose={() => setResolving(null)}
          onResolved={async () => { setResolving(null); await load(); notify?.({ title: "Issue closed", variant: "ok" }); }} />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="vb-card p-3">
      <div className="flex items-center gap-1.5 text-xs opacity-60"><Icon size={13} /> {label}</div>
      <div className="text-xl vb-heading mt-1" style={{ color: "var(--vb-text-2)" }}>{value}</div>
      {sub && <div className="text-[10px] opacity-50 mt-0.5">{sub}</div>}
    </div>
  );
}

function PhotoStrip({ urls }) {
  return (
    <div className="flex gap-2 mt-2 flex-wrap">
      {urls.map((u, i) => (
        <a key={i} href={u} target="_blank" rel="noreferrer">
          <img src={u} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6 }} />
        </a>
      ))}
    </div>
  );
}

function ResolveIssueModal({ log, onClose, onResolved }) {
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/cleaning/upload-photo?kind=resolution", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, data.url]);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Upload failed."); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!notes.trim()) { setErr("Describe how the issue was resolved."); return; }
    setBusy(true);
    try {
      await api.post(`/cleaning/logs/${log.id}/resolve-issue`, { resolution_notes: notes, resolution_photos: photos });
      onResolved();
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail) || "Failed to close issue."); }
    finally { setBusy(false); }
  };

  return (
    <div className="vb-modal-bg" onClick={onClose} data-testid="resolve-issue-modal-bg">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }} data-testid="resolve-issue-modal">
        <h2 className="vb-heading text-xl mb-1">Resolve cleaning issue</h2>
        <p className="text-sm opacity-70 mb-3">{log.equipment_name} — {log.issue?.description}</p>
        <form onSubmit={submit} className="grid gap-4">
          {err && <div className="vb-pill err">{err}</div>}
          <div>
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Resolution / corrective action *</label>
            <textarea className="vb-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Replaced door seal, verified temperature stable." data-testid="resolve-notes" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Photos (optional)</label>
            <div className="flex flex-wrap gap-2 items-center">
              {photos.map((u, i) => (
                <div key={i} className="relative" style={{ width: 60, height: 60 }}>
                  <img src={u} alt="" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8 }} />
                  <button type="button" onClick={() => setPhotos((p) => p.filter((_, idx) => idx !== i))}
                    className="absolute -top-2 -right-2 rounded-full" style={{ background: "var(--vb-error)", color: "#fff", width: 20, height: 20, lineHeight: 0 }}><Trash2 size={11} /></button>
                </div>
              ))}
              <label className="vb-btn ghost sm" style={{ cursor: "pointer" }} data-testid="resolve-photo-add">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} Add photo
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy || uploading} data-testid="resolve-submit">
              {busy ? "Closing…" : "Close issue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}



// ─── Maintenance row (interleaved into the same timeline) ──────────
function MaintenanceRow({ log }) {
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
  return (
    <div
      className="vb-card p-4"
      style={{ borderLeft: "3px solid #6b7bd0" }}
      data-testid={`maintenance-log-${log.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="vb-pill text-[10px] inline-flex items-center gap-1"
              style={{ background: "#e3e7fa", color: "#3f4b8f" }}>
              <Wrench size={10} /> Maintenance
            </span>
            <strong>{log.equipment_name}</strong>
            <span className="vb-pill ok text-[10px]">{log.reason_label || log.reason}</span>
            {log.verified_at
              ? <span className="vb-pill ok text-[10px] inline-flex items-center gap-1">
                  <ShieldCheck size={10} /> Verified by {log.verified_by_name}
                </span>
              : <span className="vb-pill warn text-[10px]">Awaiting supervisor verify</span>}
          </div>
          <div className="text-xs opacity-70 mt-1">
            {fmt(log.performed_at)} · by {log.performed_by_name || "—"}
          </div>
          {log.notes && <p className="text-sm mt-1 mb-0">{log.notes}</p>}
          {log.photos?.length > 0 && (
            <div className="flex gap-1 mt-2 flex-wrap">
              {log.photos.map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noreferrer">
                  <img src={u} alt="" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 6 }} />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Log Maintenance modal ─────────────────────────────────────────
// Reasons that only apply to Band-Saw-style non-temp equipment. Fridges,
// freezers etc. still see the generic subset. Keeping this on the
// frontend so we can extend per-type in one place without a schema
// migration.
const BAND_SAW_REASONS = ["scheduled", "wear", "break", "tracking_tension", "sanitation"];
const GENERIC_REASONS  = ["scheduled", "wear", "break"];

function reasonsForEquipment(eq) {
  const type = (eq?.type_name || eq?.equipment_type_name || "").toLowerCase();
  if (type.includes("band saw") || type.includes("bansaw")) return BAND_SAW_REASONS;
  return GENERIC_REASONS;
}

function LogMaintenanceForm({ equipment, defaultEqId, reasonLabels, performerDefault, onSubmit, onClose }) {
  const [eqId, setEqId] = useState(defaultEqId || (equipment[0]?.id || ""));
  const selectedEq = useMemo(() => equipment.find((e) => e.id === eqId), [equipment, eqId]);
  const applicableReasons = useMemo(() => reasonsForEquipment(selectedEq), [selectedEq]);
  const [reason, setReason] = useState("scheduled");
  useEffect(() => {
    // If the current reason isn't valid for the newly-selected
    // equipment (e.g. user picked a fridge after Band Saw), snap
    // back to "scheduled".
    if (!applicableReasons.includes(reason)) setReason("scheduled");
  }, [applicableReasons, reason]);
  const [performedAt, setPerformedAt] = useState(new Date().toISOString().slice(0, 16));
  const [performedBy, setPerformedBy] = useState(performerDefault);
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/cleaning/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) setPhotos((p) => [...p, data.url]);
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!eqId || !performedBy.trim()) return;
    setBusy(true);
    try {
      await onSubmit({
        equipment_id: eqId,
        reason,
        performed_at: new Date(performedAt).toISOString(),
        performed_by_name: performedBy.trim(),
        notes: notes.trim(),
        photos,
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="vb-modal-bg" onClick={onClose} data-testid="log-maintenance-bg">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}
        data-testid="log-maintenance-modal">
        <h2 className="vb-heading text-xl mb-1">Log maintenance</h2>
        <p className="text-sm opacity-70 mb-3">
          Record servicing that isn&apos;t a routine clean — blade changes, tension checks, sanitation cycles, breakdown repairs.
        </p>
        <form onSubmit={submit} className="grid gap-3">
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Equipment *</label>
            <select className="vb-input" value={eqId} onChange={(e) => setEqId(e.target.value)}
              data-testid="maint-equipment">
              {equipment.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Reason *</label>
              <select className="vb-input" value={reason} onChange={(e) => setReason(e.target.value)}
                data-testid="maint-reason">
                {applicableReasons.map((k) => (
                  <option key={k} value={k}>{reasonLabels[k] || k}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Date & time *</label>
              <input type="datetime-local" className="vb-input"
                value={performedAt} onChange={(e) => setPerformedAt(e.target.value)}
                data-testid="maint-performed-at" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Performed by *</label>
            <input className="vb-input" value={performedBy} onChange={(e) => setPerformedBy(e.target.value)}
              placeholder="Full name" data-testid="maint-performed-by" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Notes (optional)</label>
            <textarea className="vb-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="maint-notes" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Photos (optional)</label>
            <div className="flex flex-wrap gap-2 items-center">
              {photos.map((u, i) => (
                <div key={i} className="relative" style={{ width: 60, height: 60 }}>
                  <img src={u} alt="" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8 }} />
                  <button type="button" onClick={() => setPhotos((p) => p.filter((_, idx) => idx !== i))}
                    className="absolute -top-2 -right-2 rounded-full" style={{ background: "var(--vb-error)", color: "#fff", width: 20, height: 20, lineHeight: 0 }}>
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
              <label className="vb-btn ghost sm" style={{ cursor: "pointer" }} data-testid="maint-add-photo">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} Add photo
                <input type="file" accept="image/*" style={{ display: "none" }}
                  onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-1">
            <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy || uploading}
              data-testid="maint-submit">
              {busy ? "Saving…" : "Save maintenance record"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
